import { z } from 'zod'
import { zId, zIdNullable, zIsoDate } from '../common'

/**
 * Campaign-binding analytics (read-only).
 * Paths: GET /ads/campaign_bindings/:id/{timeseries,search_terms,keywords,
 * movers,activity,devices,conversions_by_action}
 *
 * Money is always micros. Ratios (ctr, impression share) are unit intervals 0–1.
 * Paginated analytics lists use { rows, total, page, perPage } — NOT the fleet
 * listSchema meta shape (no totalPages; the SPA computes it).
 *
 * Wire shapes observed live (2026-07-17):
 * - hour:  { timeseries: { hours: [...], stale?: bool }, granularity?: "hour" }
 * - day/week: { timeseries: [...buckets], granularity?: "day"|"week" }
 */

const zGoogleId = z.union([z.string(), z.number()]).transform(String)

export const TIMESERIES_GRANULARITIES = ['day', 'week', 'hour'] as const
export type TimeseriesGranularity = (typeof TIMESERIES_GRANULARITIES)[number]

export const MOVER_METRICS = ['cost', 'clicks', 'conversions'] as const
export type MoverMetric = (typeof MOVER_METRICS)[number]

export const MOVER_ENTITY_TYPES = ['search_term', 'keyword'] as const
export type MoverEntityType = (typeof MOVER_ENTITY_TYPES)[number]

// ── Shared metric atoms ─────────────────────────────────────────────────────

/** Core performance counters + server-derived ratios/CPC/CPA. */
const zMetricCore = z.object({
  clicks: z.number().default(0),
  impressions: z.number().default(0),
  costMicros: z.number().default(0),
  conversions: z.number().default(0),
  // null when the denominator is zero; may be absent on partial Google rows
  ctr: z.number().nullish().default(null),
  avgCpcMicros: z.number().nullish().default(null),
  costPerConvMicros: z.number().nullish().default(null),
})

/** Impression-share fields — day/week only (absent on hour). */
const zImpressionShare = z.object({
  impressionShare: z.number().nullish().default(null),
  budgetLostImpressionShare: z.number().nullish().default(null),
  rankLostImpressionShare: z.number().nullish().default(null),
})

// ── timeseries ──────────────────────────────────────────────────────────────

export const zTimeseriesDayPoint = zMetricCore.merge(zImpressionShare).extend({
  date: z.string(), // YYYY-MM-DD (account TZ)
})
export type TimeseriesDayPoint = z.infer<typeof zTimeseriesDayPoint>

export const zTimeseriesHourPoint = zMetricCore.extend({
  date: z.string(), // YYYY-MM-DD
  hour: z.number().int().min(0).max(23),
})
export type TimeseriesHourPoint = z.infer<typeof zTimeseriesHourPoint>

/**
 * Day / week: top-level `timeseries` is the bucket array.
 * `granularity` may be echoed.
 */
export const zTimeseriesDayWeek = z.object({
  timeseries: z.array(zTimeseriesDayPoint),
  granularity: z.enum(['day', 'week']).optional(),
})
export type TimeseriesDayWeek = z.infer<typeof zTimeseriesDayWeek>

/**
 * Hour: top-level `timeseries` is an object `{ hours, stale }`.
 * `stale=true` means Google was unreachable and the server served the last cache.
 */
export const zTimeseriesHour = z.object({
  timeseries: z.object({
    hours: z.array(zTimeseriesHourPoint),
    stale: z.boolean().nullish().default(false),
  }),
  granularity: z.literal('hour').optional(),
})
export type TimeseriesHour = z.infer<typeof zTimeseriesHour>

// ── search_terms / keywords (paginated, non-standard meta) ───────────────────

const zAnalyticsPage = z.object({
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
})

export const zSearchTermRow = zMetricCore.extend({
  searchTerm: z.string(),
  matchType: z.string().nullish().default(null),
  adGroupName: z.string().nullish().default(null),
  adGroupId: zGoogleId.nullish().default(null),
})
export type SearchTermRow = z.infer<typeof zSearchTermRow>

export const zSearchTermsPage = zAnalyticsPage.extend({
  rows: z.array(zSearchTermRow),
})
export type SearchTermsPage = z.infer<typeof zSearchTermsPage>

export const zSearchTermsResponse = z.object({
  searchTerms: zSearchTermsPage,
})
export type SearchTermsResponse = z.infer<typeof zSearchTermsResponse>

export const zKeywordRow = zMetricCore.extend({
  text: z.string(),
  matchType: z.string().nullish().default(null),
  status: z.string().nullish().default(null), // ENABLED | PAUSED | …
  adGroupName: z.string().nullish().default(null),
  adGroupId: zGoogleId.nullish().default(null),
  qualityScore: z.number().nullish().default(null),
})
export type KeywordRow = z.infer<typeof zKeywordRow>

export const zKeywordsPage = zAnalyticsPage.extend({
  rows: z.array(zKeywordRow),
})
export type KeywordsPage = z.infer<typeof zKeywordsPage>

export const zKeywordsResponse = z.object({
  keywords: zKeywordsPage,
})
export type KeywordsResponse = z.infer<typeof zKeywordsResponse>

// ── movers ──────────────────────────────────────────────────────────────────

export const zMoverRow = z.object({
  term: z.string(),
  current: z.number().default(0),
  previous: z.number().default(0),
  // micros when metric=cost; raw count otherwise
  delta: z.number().default(0),
})
export type MoverRow = z.infer<typeof zMoverRow>

export const zMoversResponse = z.object({
  movers: z.array(zMoverRow),
})
export type MoversResponse = z.infer<typeof zMoversResponse>

// ── activity ────────────────────────────────────────────────────────────────

export const zActivityItem = z.object({
  id: zId,
  at: zIsoDate,
  kind: z.string(), // e.g. add_negative_keyword
  status: z.string(), // applied | failed | …
  ruleId: zIdNullable.nullish().default(null),
  summary: z.string().nullish().default(null),
})
export type ActivityItem = z.infer<typeof zActivityItem>

export const zActivityResponse = z.object({
  activity: z.array(zActivityItem),
})
export type ActivityResponse = z.infer<typeof zActivityResponse>

// ── devices ─────────────────────────────────────────────────────────────────

export const zDeviceRow = zMetricCore.extend({
  device: z.string(), // MOBILE | DESKTOP | TABLET
})
export type DeviceRow = z.infer<typeof zDeviceRow>

export const zDevicesResponse = z.object({
  devices: z.array(zDeviceRow),
})
export type DevicesResponse = z.infer<typeof zDevicesResponse>

// ── conversions_by_action ───────────────────────────────────────────────────

export const zConversionByActionRow = z.object({
  conversionActionId: zGoogleId,
  name: z.string().nullish().default(null),
  conversions: z.number().default(0),
  // true when this action counts for the binding's conversion gate
  linkedToBinding: z.boolean().default(false),
})
export type ConversionByActionRow = z.infer<typeof zConversionByActionRow>

export const zConversionsByActionResponse = z.object({
  conversionsByAction: z.array(zConversionByActionRow),
})
export type ConversionsByActionResponse = z.infer<typeof zConversionsByActionResponse>
